/**
 * Okey Taş Komponenti
 * 3D Kazınmış Görünümlü Gerçekçi Türk Okey Taşı
 */
import { useState } from "react";
import { cn } from "../../lib/utils";

// Gerçek okey taşı renkleri - Türk Okey taşları
const TILE_COLORS = {
  red: { text: "#DC2626", shadow: "#991B1B", bg: "#FEE2E2" },
  blue: { text: "#2563EB", shadow: "#1E40AF", bg: "#DBEAFE" },
  black: { text: "#1F2937", shadow: "#111827", bg: "#F3F4F6" },
  yellow: { text: "#D97706", shadow: "#B45309", bg: "#FEF3C7" },
  joker: { text: "#7C3AED", shadow: "#5B21B6", bg: "#EDE9FE" }
};

export default function OkeyTile({ 
  tile, 
  size = "md",
  selected = false,
  onClick,
  disabled = false,
  faceDown = false,
  className = "",
  style = {}
}) {
  const [isFlipped, setIsFlipped] = useState(false);
  
  if (!tile) return null;

  const colorConfig = TILE_COLORS[tile.color] || TILE_COLORS.black;
  
  // Boyutlar - taş boyutu aynı, rakamlar daha büyük
  const sizes = {
    xs: { width: 30, height: 42, fontSize: 22, radius: 6 },
    sm: { width: 38, height: 52, fontSize: 28, radius: 7 },
    md: { width: 44, height: 60, fontSize: 34, radius: 8 },
    lg: { width: 52, height: 70, fontSize: 40, radius: 9 },
    xl: { width: 60, height: 82, fontSize: 48, radius: 10 }
  };
  
  const s = sizes[size] || sizes.md;

  // Sağ tık ile taşı ters çevir (sadece Okey taşları için)
  const handleContextMenu = (e) => {
    if (tile.is_okey) {
      e.preventDefault();
      setIsFlipped(!isFlipped);
    }
  };

  // Ters çevrilmiş Okey taşı - BEYAZ arka yüz
  if (isFlipped && tile.is_okey) {
    return (
      <div
        onContextMenu={handleContextMenu}
        className={cn("relative", className)}
        style={{
          width: s.width,
          height: s.height,
          borderRadius: s.radius,
          background: `linear-gradient(180deg, #FFFFFF 0%, #F8F5F0 50%, #EDE8E0 100%)`,
          boxShadow: `
            0 4px 8px rgba(0,0,0,0.2),
            0 2px 4px rgba(0,0,0,0.1),
            inset 0 1px 0 rgba(255,255,255,1),
            inset 0 -2px 4px rgba(0,0,0,0.08)
          `,
          border: "1px solid #C9C0B5",
          cursor: "context-menu",
          ...style
        }}
        title="Sağ tık ile çevir"
      />
    );
  }

  // Kapalı taş (rakip taşları)
  if (faceDown) {
    return (
      <div
        className={cn("relative", className)}
        style={{
          width: s.width,
          height: s.height,
          borderRadius: s.radius,
          background: "linear-gradient(145deg, #D4B896 0%, #A67C52 30%, #8B5A2B 70%, #654321 100%)",
          boxShadow: `
            0 4px 8px rgba(0,0,0,0.4),
            0 2px 4px rgba(0,0,0,0.3),
            inset 0 2px 4px rgba(255,255,255,0.2),
            inset 0 -3px 6px rgba(0,0,0,0.3)
          `,
          border: "1px solid #4A3520",
          ...style
        }}
      >
        {/* Arka yüz deseni */}
        <div 
          className="absolute inset-2 rounded-sm"
          style={{
            background: `
              radial-gradient(ellipse at 30% 20%, rgba(255,255,255,0.15) 0%, transparent 50%),
              linear-gradient(135deg, rgba(139,90,43,0.3) 25%, transparent 25%, transparent 50%, rgba(139,90,43,0.3) 50%, rgba(139,90,43,0.3) 75%, transparent 75%)
            `,
            backgroundSize: "100% 100%, 8px 8px"
          }}
        />
      </div>
    );
  }

  // Açık taş - Normal taş görünümü (Okey dahil - yıldız işareti yok)
  return (
    <div
      onClick={disabled ? undefined : onClick}
      onContextMenu={handleContextMenu}
      className={cn(
        "relative transition-all duration-150",
        !disabled && onClick && "cursor-pointer",
        selected && "-translate-y-3 scale-105",
        disabled && "opacity-60 cursor-not-allowed",
        className
      )}
      style={{
        width: s.width,
        height: s.height,
        borderRadius: s.radius,
        background: `linear-gradient(180deg, #FFFFFF 0%, #F8F5F0 50%, #EDE8E0 100%)`,
        boxShadow: selected 
          ? `
            0 8px 16px rgba(0,0,0,0.3),
            0 0 0 2px #4DFFFF,
            inset 0 1px 0 rgba(255,255,255,1),
            inset 0 -2px 4px rgba(0,0,0,0.1)
          `
          : `
            0 4px 8px rgba(0,0,0,0.2),
            0 2px 4px rgba(0,0,0,0.1),
            inset 0 1px 0 rgba(255,255,255,1),
            inset 0 -2px 4px rgba(0,0,0,0.08)
          `,
        border: "1px solid #C9C0B5",
        cursor: tile.is_okey ? "context-menu" : undefined,
        ...style
      }}
      title={tile.is_okey ? "Sağ tık ile ters çevir" : ""}
    >
      {/* Taş yüzeyi */}
      <div 
        className="absolute inset-0 flex items-center justify-center"
        style={{ borderRadius: s.radius }}
      >
        {/* Sayı veya Joker */}
        {tile.color === "joker" ? (
          <span 
            style={{ 
              fontSize: s.fontSize * 1.2,
              fontWeight: 900,
              fontFamily: "'Arial Black', 'Helvetica Neue', sans-serif",
              color: colorConfig.text,
              textShadow: `1px 1px 2px ${colorConfig.shadow}`
            }}
          >
            ★
          </span>
        ) : (
          <span 
            style={{ 
              fontSize: s.fontSize,
              fontWeight: 800,
              fontFamily: "'Arial Black', 'Helvetica Neue', sans-serif",
              color: colorConfig.text,
              textShadow: `1px 1px 1px rgba(255,255,255,0.5), 0 1px 2px ${colorConfig.shadow}40`,
              lineHeight: 1
            }}
          >
            {tile.number}
          </span>
        )}
      </div>
    </div>
  );
}
